import dayjs from 'dayjs';

import { injectLabelVariables } from './labelVariables';

// Copied from components/HighCharts/utils.js so the renderer stays a pure,
// self-contained module. The published renderer keeps its own copy until the
// contract slice (ticket 08) removes the now-dead originals.
const HEATMAP_COLOR_STOPS_BLUE = [
    [0, '#DFEEF5'],
    [0.2, '#97C7DC'],
    [0.4, '#5A9EBC'],
    [0.6, '#14759A'],
    [0.8, '#004C68'],
    [1, '#0C2632'],
];

const HEATMAP_COLOR_STOPS_RED = [
    [0, '#FBE0E2'],
    [0.2, '#FFA2A8'],
    [0.4, '#FF5468'],
    [0.6, '#D21132'],
    [0.8, '#900002'],
    [1, '#540000'],
];

const HEATMAP_COLOR_STOPS_BY_SCHEME = {
    blue: HEATMAP_COLOR_STOPS_BLUE,
    red: HEATMAP_COLOR_STOPS_RED,
};

const dynamicSort =
    (order = 'asc', sortType = null) =>
    (a, b) => {
        if (sortType === 'date') {
            return order === 'asc'
                ? new Date(a) - new Date(b)
                : new Date(b) - new Date(a);
        }

        if (sortType === 'number') {
            const numberA = Number(String(a).replace(/[^\d]/g, ''));
            const numberB = Number(String(b).replace(/[^\d]/g, ''));
            return order === 'asc' ? numberA - numberB : numberB - numberA;
        }

        return order === 'asc'
            ? String(a).localeCompare(String(b))
            : String(b).localeCompare(String(a));
    };

const formatDateValue = (value, formattingType) => {
    switch (formattingType) {
        case 'datetime':
            return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        case 'date':
            return dayjs(value).format('YYYY-MM-DD');
        case 'year':
            return dayjs(value).format('YYYY');
        case 'monthday':
            return dayjs(value).format('DD/MM');
        case 'monthyear':
            return dayjs(value).format('MM/YYYY');
        case 'human-readable':
            return dayjs(value).format('MMM D, YYYY');
        default:
            return value;
    }
};

// Sort an axis's categories and produce the map from the aggregate's original
// index to the sorted index, mirroring the published renderer's inline sort.
const sortAxis = (categories, ordering, sortingType) => {
    if (ordering === 'none') {
        return { categories, indexMap: categories.map((_, index) => index) };
    }
    const sorted = [...categories].sort(dynamicSort(ordering, sortingType));
    const indexMap = categories.map((category) => sorted.indexOf(category));
    return { categories: sorted, indexMap };
};

const formatCategories = (categories, formatting) =>
    formatting !== 'none'
        ? categories.map((category) => formatDateValue(category, formatting))
        : categories;

const HEATMAP_ROW_HEIGHT = 50;

export const buildHeatmapChartOptions = (chartBlock, rows) => {
    // Heatmap is single-series; the aggregate is produced caller-side and
    // shared with the published data client (aggregateHeatmapItems).
    const aggregate = (rows && rows[0]) || {
        xAxisCategories: [],
        yAxisCategories: [],
        data: [],
    };

    const { categories: sortedX, indexMap: xIndexMap } = sortAxis(
        aggregate.xAxisCategories || [],
        chartBlock.xAxisCategoryOrdering,
        chartBlock.xAxisCategorySortingType
    );
    const { categories: sortedY, indexMap: yIndexMap } = sortAxis(
        aggregate.yAxisCategories || [],
        chartBlock.yAxisCategoryOrdering,
        chartBlock.yAxisCategorySortingType
    );

    const data = (aggregate.data || []).map(([x, y, value]) => [
        xIndexMap[x],
        yIndexMap[y],
        value,
    ]);

    const xAxisCategories = formatCategories(
        sortedX,
        chartBlock.xAxisLabelFormattting
    );
    const yAxisCategories = formatCategories(
        sortedY,
        chartBlock.yAxisLabelFormattting
    );

    const marginTop = chartBlock.isDesktop ? 10 : 24;
    const chartHeight = Math.max(
        300,
        sortedY.length * HEATMAP_ROW_HEIGHT + marginTop + 80
    );

    const hasDecimalFmt =
        Number.isInteger(chartBlock.decimalPlaces) &&
        chartBlock.decimalPlaces >= 0;
    const decimalSuffix = hasDecimalFmt ? `:.${chartBlock.decimalPlaces}f` : '';

    return {
        title: { text: null },
        ...(chartBlock.exporting ? { exporting: chartBlock.exporting } : {}),
        chart: {
            type: 'heatmap',
            height: chartHeight,
            marginTop,
            plotBorderWidth: 1,
            scrollablePlotArea: {
                minWidth: sortedX.length * 80,
                scrollPositionX: 0,
            },
        },
        xAxis: {
            categories: xAxisCategories,
            title: {
                text: injectLabelVariables(
                    chartBlock.xAxisLabel,
                    chartBlock.labelVariables
                ),
            },
            lineWidth: 0,
        },
        yAxis: {
            categories: yAxisCategories,
            title: {
                text: injectLabelVariables(
                    chartBlock.yAxisLabel,
                    chartBlock.labelVariables
                ),
            },
            reversed: true,
        },
        colorAxis: {
            min: 0,
            stops:
                HEATMAP_COLOR_STOPS_BY_SCHEME[chartBlock.colorScheme] ||
                HEATMAP_COLOR_STOPS_BLUE,
        },
        legend: {
            align: 'center',
            layout: 'horizontal',
            verticalAlign: 'bottom',
            padding: 0,
            marginTop: 12,
            marginBottom: 12,
            symbolWidth: 280,
            symbolHeight: 12,
            navigation: {
                enabled: false,
            },
        },
        tooltip: {
            format:
                '<b>{series.xAxis.categories.(point.x)}</b><br>' +
                `<b>{point.value${decimalSuffix}}</b> on <br>` +
                '<b>{series.yAxis.categories.(point.y)}</b>',
        },
        series: [
            {
                name: chartBlock.title,
                borderWidth: 1,
                borderColor: '#e6e6e6',
                data,
                dataLabels: {
                    enabled: true,
                    color: 'contrast',
                    style: {
                        fontSize: '14px',
                        fontWeight: 'normal',
                        textOutline: 'none',
                    },
                    ...(hasDecimalFmt
                        ? {
                              format: `{point.value:.${chartBlock.decimalPlaces}f}`,
                          }
                        : {}),
                },
            },
        ],
        responsive: {
            rules: [
                {
                    condition: {
                        maxWidth: 500,
                    },
                    chartOptions: {
                        xAxis: {
                            labels: {
                                rotation: -45,
                                style: {
                                    fontSize: '10px',
                                },
                            },
                        },
                        yAxis: {
                            labels: {
                                style: {
                                    fontSize: '10px',
                                },
                            },
                        },
                        series: [
                            {
                                dataLabels: {
                                    style: {
                                        fontSize: '11px',
                                    },
                                },
                            },
                        ],
                    },
                },
            ],
        },
    };
};
